package com.cybergstudios.rtc.engine

import com.cybergstudios.rtc.math.Vec2
import com.cybergstudios.rtc.world.Paddle
import com.cybergstudios.rtc.world.Polygon
import kotlinx.browser.window
import org.w3c.dom.CanvasLineJoin
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLElement
import org.w3c.dom.ROUND

/**
 * The ContextWeb is platform specific to html, css, and javascript.
 */
data class ContextWeb(private val ctx: CanvasRenderingContext2D) : Context {

    override fun clearCanvas() {
        ctx.clearRect(
            0.0,
            0.0,
            ctx.canvas.width.toDouble(),
            ctx.canvas.height.toDouble()
        )
    }

    override fun renderGameWin(state: GameState) {
        clearCanvas()
        ctx.save()
        ctx.fillStyle = "rgba(0,255,0,1.0)"
        ctx.font = "96px serif"
        ctx.fillText("You win!", 100.0, state.canvasDimension.originY)
        ctx.font = "28px serif"
        ctx.fillText("Level 2 is not ready yet", 150.0, state.canvasDimension.originY + 50.0)
        ctx.restore()
    }

    override fun renderGameLost(state: GameState) {
        clearCanvas()
        ctx.save()
        ctx.fillStyle = "rgba(0,255,0,1.0)"
        ctx.font = "96px serif"
        ctx.fillText("You Lost!", 100.0, state.canvasDimension.originY)
        ctx.restore()
    }

    override fun scheduleNextFrame(engine: Engine) {
        window.requestAnimationFrame { engine.loop() }
    }

    override fun getTick(): Double = window.performance.now()

    override fun renderPolygon(polygon: Polygon, state: GameState) {
        if (!polygon.enabled) return
        val origin = Vec2(state.canvasDimension.originX, state.canvasDimension.originY)
        neonPolygon(
            polygon.points.map { origin.add(Vec2(it.x, -it.y)) },
            polygon.color.r,
            polygon.color.g,
            polygon.color.b
        )
    }

    override fun renderPaddlePlayer(state: GameState) = renderPaddle(state.paddlePlayer, state)

    override fun renderPaddleOpponent(state: GameState) = renderPaddle(state.paddleOpponent, state)

    private fun renderPaddle(paddle: Paddle, state: GameState) {
        neonRectangle(
            state.canvasDimension.originX + paddle.x,
            state.canvasDimension.originY - paddle.y,
            paddle.width,
            paddle.height,
            paddle.color.r,
            paddle.color.g,
            paddle.color.b
        )
    }

    private fun neonRectangle(x: Double, y: Double, w: Double, h: Double, r: Double, g: Double, b: Double) {
        ctx.save();

        // bloom effect
        ctx.globalCompositeOperation = "lighter"
        ctx.lineJoin = CanvasLineJoin.ROUND

        ctx.shadowColor = "rgb($r,$g,$b)"
        ctx.shadowBlur = 10.0
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 7.5
        drawRectangle(x, y, w, h,1.5)
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 6.0
        drawRectangle(x, y, w, h, 1.5)
        ctx.strokeStyle = "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 4.5
        drawRectangle(x, y, w, h, 1.5)
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 3.0
        drawRectangle(x, y, w, h, 1.5)
        ctx.strokeStyle= "#fff"
        ctx.lineWidth = 1.5
        drawRectangle(x, y, w, h, 1.5);

        ctx.restore();
    }

    private fun drawRectangle(x: Double, y: Double, w: Double, h: Double, border: Double) {
        ctx.beginPath();
        ctx.moveTo(x + border, y)
        ctx.lineTo(x + w - border, y)
        ctx.quadraticCurveTo(x + w - border, y, x + w, y + border)
        ctx.lineTo(x + w, y + h - border)
        ctx.quadraticCurveTo(x + w, y + h - border,  x + w - border, y + h)
        ctx.lineTo(x + border, y + h)
        ctx.quadraticCurveTo(x + border, y + h, x, y + h - border)
        ctx.lineTo(x, y + border)
        ctx.quadraticCurveTo(x, y + border, x + border, y)
        ctx.closePath()
        ctx.stroke()
    }

    override fun renderBall(state: GameState) {
        neonBall(
            state.canvasDimension.originX + state.ball.x,
            state.canvasDimension.originY - state.ball.y,
            state.ball.radius,
            state.ball.color.r,
            state.ball.color.g,
            state.ball.color.b
        )
    }

    override fun renderShipsMenu(playerLives: Int) {
        val lives = window.document.getElementById("menu_ships") as HTMLElement
        lives.innerText = "Ships $playerLives"
    }

    override fun renderPlayerScore(playerPoints: Int) {
        val points = window.document.getElementById("menu_score") as HTMLElement
        points.innerText = "Score $playerPoints"
    }

    private fun neonPolygon(points: List<Vec2>, r: Double, g: Double, b: Double) {
        ctx.save();

        // bloom effect
        ctx.globalCompositeOperation = "lighter"
        ctx.lineJoin = CanvasLineJoin.ROUND

        ctx.shadowColor = "rgb($r,$g,$b)"
        ctx.shadowBlur = 10.0
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 7.5
        drawPolygon(points)
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 6.0
        drawPolygon(points)
        ctx.strokeStyle = "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 4.5
        drawPolygon(points)
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        ctx.lineWidth = 3.0
        drawPolygon(points)
        ctx.strokeStyle= "#fff"
        ctx.lineWidth = 1.5
        drawPolygon(points)

        ctx.restore();
    }

    private fun drawPolygon(points: List<Vec2>) {
        ctx.beginPath();
        val p0 = points[0]
        ctx.moveTo(p0.x, p0.y)
        for (i in 1..< points.size) {
            val p = points[i]
            ctx.lineTo(p.x, p.y)
        }
        ctx.closePath()
        ctx.stroke()
    }

    private fun neonBall(x: Double, y: Double, radius: Double, r: Double, g: Double, b: Double) {
        ctx.save();

        // bloom effect
        ctx.globalCompositeOperation = "lighter"

        ctx.shadowColor = "rgb($r,$g,$b)"
        ctx.shadowBlur = 8.0
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        drawBall(x, y, radius)
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        drawBall(x, y, radius)
        ctx.strokeStyle = "rgba($r,$g,$b,0.2)"
        drawBall(x, y, radius)
        ctx.strokeStyle= "rgba($r,$g,$b,0.2)"
        drawBall(x, y, radius)
        ctx.strokeStyle= "#fff"
        drawBall(x, y, radius)

        ctx.restore();
    }

    private fun drawBall(x: Double, y: Double, radius: Double) {
        ctx.beginPath()
        ctx.arc(x, y, radius, 0.0, 2 * kotlin.math.PI, false)
        ctx.fill()
        //ctx.stroke()
    }
}
